import { ThemeEvents, CartUpdateEvent, CartUpdateSources } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/** Line item property used to tag and identify auto-added gift products. */
const FREE_GIFT_PROPERTY = '_free_gift';

/** Event source identifier to avoid re-sync loops after our own cart mutations. */
const SYNC_SOURCE = CartUpdateSources.freeGiftsSync;

/**
 * @typedef {Object} FreeGiftStep
 * @property {string} id - Metaobject handle (stored on line item property)
 * @property {number} thresholdCents - Minimum eligible subtotal in cents
 * @property {number} variantId - Gift variant to add
 * @property {number} productId - Gift product id
 */

/**
 * @typedef {Object} CartLine
 * @property {number} variant_id
 * @property {number} quantity
 * @property {string} key
 * @property {number} final_line_price
 * @property {Record<string, string>} [properties]
 */

/**
 * @typedef {Object} CartPayload
 * @property {CartLine[]} items
 * @property {number} item_count
 */

/**
 * @typedef {Object} CartMutationResponse
 * @property {CartLine[]} [items]
 * @property {number} [item_count]
 * @property {Record<string, string>} [sections]
 */

/**
 * Keeps cart line items in sync with progress_bar_step gift thresholds.
 * Adds, updates, or removes gift products via the Cart AJAX API.
 */
class CartFreeGiftsController {
  /** @type {FreeGiftStep[]} */
  #steps = [];

  /** @type {boolean} */
  #syncing = false;

  /** @type {boolean} */
  #pendingSync = false;

  /** @type {CartPayload | undefined} */
  #pendingCart = undefined;

  /** @type {((cart?: CartPayload) => void) & { cancel(): void } | null} */
  #debouncedSync = null;

  /** @type {CartPayload | undefined} */
  #lastObservedCart = undefined;

  /**
   * Reads gift step JSON from the config element.
   * Safari requires HTMLTemplateElement.content; .innerHTML on <template> is often empty.
   * @param {HTMLElement | null} element
   * @returns {string}
   */
  #readConfigText(element) {
    if (!element) return '';

    if (element instanceof HTMLTemplateElement) {
      return element.content.textContent?.trim() ?? '';
    }

    return element.textContent?.trim() ?? '';
  }

  /**
   * Reads step config from Liquid JSON, registers cart listeners, and runs an initial sync.
   */
  init() {
    const configText = this.#readConfigText(document.getElementById('cart-free-gift-steps-data'));
    if (!configText) return;

    try {
      const steps = JSON.parse(configText);
      if (!Array.isArray(steps) || steps.length === 0) return;
      this.#steps = steps
        .filter((step) => step?.variantId && step?.id && Number(step.thresholdCents) > 0)
        .map((step) => ({
          id: String(step.id),
          thresholdCents: Number(step.thresholdCents),
          variantId: Number(step.variantId),
          productId: Number(step.productId),
        }))
        .sort((a, b) => a.thresholdCents - b.thresholdCents);
    } catch (error) {
      console.error('[cart-free-gifts] Invalid config', error);
      return;
    }

    if (this.#steps.length === 0) return;

    this.#debouncedSync = debounce((/** @type {CartPayload | undefined} */ cart) => this.#scheduleSync(cart), 50);

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);

    this.#scheduleSync();
  }

  /**
   * Reacts to cart changes triggered elsewhere in the theme (add, qty change, discount, etc.).
   * Uses the cart payload from the event when available to skip an extra /cart.js round trip.
   * Runs immediately when gifts must be removed; otherwise debounces rapid consecutive updates.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const detail = /** @type {{ detail?: { resource?: CartPayload; data?: { source?: string } } }} */ (
      event
    ).detail;
    if (detail?.data?.source === SYNC_SOURCE) return;

    const cart = this.#normalizeCart(detail?.resource);

    if (detail?.data?.source === CartUpdateSources.freeGiftManualRemoval) {
      this.#debouncedSync?.cancel();
      if (cart) this.#lastObservedCart = cart;
      return;
    }

    if (cart) {
      this.#lastObservedCart = cart;
    }

    if (cart) {
      const { toRemove } = this.#computeActions(cart);
      if (toRemove.length > 0) {
        this.#debouncedSync?.cancel();
        this.#scheduleSync(cart);
        return;
      }
    }

    this.#debouncedSync?.cancel();
    this.#debouncedSync?.(cart);
  };

  /**
   * Queues a sync, re-running after the current one if already in progress.
   * @param {CartPayload} [cartFromEvent] - Cart JSON from a CartUpdateEvent when available
   */
  #scheduleSync(cartFromEvent) {
    if (this.#syncing) {
      this.#pendingSync = true;
      if (cartFromEvent) this.#pendingCart = cartFromEvent;
      return;
    }

    this.#sync(cartFromEvent);
  }

  /**
   * Main sync routine: compares cart state to thresholds, mutates the cart, then refreshes UI.
   * @param {CartPayload} [cartFromEvent] - Cart JSON from a CartUpdateEvent when available
   */
  async #sync(cartFromEvent) {
    if (this.#syncing) {
      this.#pendingSync = true;
      if (cartFromEvent) this.#pendingCart = cartFromEvent;
      return;
    }

    this.#syncing = true;

    try {
      const cart = cartFromEvent ?? (await this.#fetchCart());
      this.#lastObservedCart = cart;
      const { toAdd, toUpdate, toRemove } = this.#computeActions(cart);

      if (toAdd.length === 0 && toUpdate.length === 0 && toRemove.length === 0) return;

      const sectionIds = this.#getSectionIds();

      /** @type {CartMutationResponse | null} */
      let mutationResponse = null;

      if (toRemove.length > 0 || toUpdate.length > 0) {
        mutationResponse = await this.#applyUpdates(toRemove, toUpdate, sectionIds);
      }

      if (toAdd.length > 0) {
        mutationResponse = await this.#applyAdds(toAdd, sectionIds);
      }

      await this.#publishCartUpdate(mutationResponse, sectionIds);
    } catch (error) {
      console.error('[cart-free-gifts] Sync failed', error);
    } finally {
      this.#syncing = false;

      if (this.#pendingSync) {
        this.#pendingSync = false;
        const pendingCart = this.#pendingCart;
        this.#pendingCart = undefined;
        await this.#sync(pendingCart);
      }
    }
  }

  /**
   * Extracts a full cart payload from a Cart API mutation response when present.
   * @param {CartMutationResponse | null} mutationResponse
   * @returns {CartPayload | undefined}
   */
  #cartFromMutation(mutationResponse) {
    if (!mutationResponse || !Array.isArray(mutationResponse.items)) return undefined;
    if (typeof mutationResponse.item_count !== 'number') return undefined;
    return /** @type {CartPayload} */ (mutationResponse);
  }

  /**
   * Returns a cart payload when the event resource includes line items.
   * @param {unknown} resource
   * @returns {CartPayload | undefined}
   */
  #normalizeCart(resource) {
    if (!resource || typeof resource !== 'object') return undefined;
    const cart = /** @type {CartPayload} */ (resource);
    return Array.isArray(cart.items) ? cart : undefined;
  }

  /**
   * Fetches the current cart state from the Cart AJAX API.
   * @returns {Promise<CartPayload>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  /**
   * Calculates subtotal used for threshold checks, excluding gift lines.
   * @param {CartPayload} cart
   * @returns {number} Eligible subtotal in cents
   */
  #eligibleSubtotalCents(cart) {
    const giftVariantIds = new Set(this.#steps.map((step) => step.variantId));

    return cart.items.reduce((sum, item) => {
      const variantId = Number(item.variant_id);
      const isGiftLine =
        Boolean(item.properties?.[FREE_GIFT_PROPERTY]) || giftVariantIds.has(variantId);

      if (isGiftLine) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  /**
   * Determines which gift lines should be added, corrected, or removed.
   * @param {CartPayload} cart
   * @returns {{ toAdd: FreeGiftStep[], toUpdate: { key: string, quantity: number }[], toRemove: string[] }}
   */
  #computeActions(cart) {
    const eligibleSubtotal = this.#eligibleSubtotalCents(cart);

    /** @type {FreeGiftStep[]} */
    const toAdd = [];
    /** @type {{ key: string; quantity: number }[]} */
    const toUpdate = [];
    /** @type {string[]} */
    const toRemove = [];

    for (const step of this.#steps) {
      const reached = eligibleSubtotal >= step.thresholdCents;
      const giftLine = cart.items.find(
        (item) =>
          item.properties?.[FREE_GIFT_PROPERTY] === step.id &&
          Number(item.variant_id) === step.variantId
      );

      if (reached && !giftLine) {
        toAdd.push(step);
      } else if (reached && giftLine) {
        if (giftLine.quantity !== 1) {
          toUpdate.push({ key: giftLine.key, quantity: 1 });
        }
      } else if (!reached && giftLine) {
        toRemove.push(giftLine.key);
      }
    }

    return { toAdd, toUpdate, toRemove };
  }

  /**
   * Collects section IDs from cart drawer/page components for Section Rendering API updates.
   * @returns {string[]}
   */
  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  /**
   * Removes gift lines and fixes quantities via POST /cart/update.js.
   * @param {string[]} toRemove - Line item keys to set to quantity 0
   * @param {{ key: string; quantity: number }[]} toUpdate - Line item keys to correct
   * @param {string[]} sectionIds - Sections to re-render in the response
   * @returns {Promise<CartMutationResponse>}
   */
  async #applyUpdates(toRemove, toUpdate, sectionIds) {
    /** @type {Record<string, number>} */
    const updates = {};

    for (const key of toRemove) {
      updates[key] = 0;
    }

    for (const { key, quantity } of toUpdate) {
      updates[key] = quantity;
    }

    if (Object.keys(updates).length === 0) return /** @type {CartMutationResponse} */ ({});

    /** @type {Record<string, unknown>} */
    const body = { updates };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify(body) })
    );

    const data = await response.json();
    if (data.errors) throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed');

    return data;
  }

  /**
   * Adds missing gift variants via POST /cart/add.js with the _free_gift line property.
   * @param {FreeGiftStep[]} toAdd - Steps whose gifts should be in the cart
   * @param {string[]} sectionIds - Sections to re-render in the response
   * @returns {Promise<CartMutationResponse>}
   */
  async #applyAdds(toAdd, sectionIds) {
    /** @type {Record<string, unknown>} */
    const body = {
      items: toAdd.map((step) => ({
        id: step.variantId,
        quantity: 1,
        properties: {
          [FREE_GIFT_PROPERTY]: step.id,
        },
      })),
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', { body: JSON.stringify(body) })
    );

    const data = await response.json();
    if (data.status && data.message) throw new Error(data.message);

    return data;
  }

  /**
   * Morphs cart sections and broadcasts a cart update so the drawer reflects gift changes.
   * @param {CartMutationResponse | null} mutationResponse - Last Cart API response with sections HTML
   * @param {string[]} sectionIds - Section IDs to morph
   */
  async #publishCartUpdate(mutationResponse, sectionIds) {
    const cart = this.#cartFromMutation(mutationResponse) ?? (await this.#fetchCart());

    if (mutationResponse?.sections && sectionIds.length > 0) {
      for (const sectionId of sectionIds) {
        const html = mutationResponse.sections[sectionId];
        if (html) morphSection(sectionId, html);
      }
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'cart-free-gifts', {
        source: SYNC_SOURCE,
        itemCount: cart.item_count,
        sections: mutationResponse?.sections,
      })
    );
  }
}

const controller = new CartFreeGiftsController();

/** Bootstrap: wait for DOM when needed, then initialize gift sync. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => controller.init());
} else {
  controller.init();
}
